import { AddOnTextInput, Panel, RangeSlider, Typography } from "@/components/atoms";
import { clsx } from "@/utils";
import { formatBookingValue, MIN_BOOKINGS, MAX_BOOKINGS } from "./helpers/pricingCalculations";

interface EmbedBookingPanelProps {
  monthlyBookings: number;
  onMonthlyBookingsChange: (value: number) => void;
}

export const EmbedBookingPanel = ({ monthlyBookings, onMonthlyBookingsChange }: EmbedBookingPanelProps) => {
  const getTooltipStyle = () => {
    const percentage = ((monthlyBookings - MIN_BOOKINGS) / (MAX_BOOKINGS - MIN_BOOKINGS)) * 100;

    if (percentage <= 15) {
      return {
        left: `calc(${percentage}% + 12px)`,
        transform: "translateX(0)",
      };
    } else if (percentage >= 85) {
      return {
        left: `calc(${percentage}% - 12px)`,
        transform: "translateX(-100%)",
      };
    } else {
      return {
        left: `${percentage}%`,
        transform: "translateX(-50%)",
      };
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(Math.max(Number(e.target.value) || 0, MIN_BOOKINGS), MAX_BOOKINGS);
    onMonthlyBookingsChange(value);
  };

  const handleSliderChange = (value: number | number[]) => {
    onMonthlyBookingsChange(value as number);
  };

  return (
    <Panel className="flex flex-col gap-6">
      {/* Header with Monthly Bookings Input */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <Typography variant="action">Average Monthly Booking (2025)</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            A guide to help you estimate your plan needs based on monthly activity
          </Typography>
        </div>
        <AddOnTextInput
          type="number"
          value={String(monthlyBookings)}
          onChange={handleInputChange}
          startAddOn="Monthly Bookings"
          className="w-full sm:w-auto"
          min={MIN_BOOKINGS}
          max={MAX_BOOKINGS}
        />
      </div>

      {/* Slider section */}
      <div className="flex flex-col gap-4">
        {/* Min and Max labels */}
        <div className="flex flex-row justify-between text-neutral-dark-gray">
          <Typography variant="paragraph">{MIN_BOOKINGS}</Typography>
          <Typography variant="paragraph">{formatBookingValue(MAX_BOOKINGS)}</Typography>
        </div>

        {/* Slider with floating tooltip */}
        <div className="relative">
          <RangeSlider min={MIN_BOOKINGS} max={MAX_BOOKINGS} value={monthlyBookings} onChange={handleSliderChange} />

          {/* Floating tooltip that follows the slider thumb */}
          <div
            className={clsx(
              "absolute top-5 rounded-lg border border-neutral-mid-gray bg-white px-3 py-1.5 shadow-sm",
              "pointer-events-none z-10 whitespace-nowrap"
            )}
            style={getTooltipStyle()}
          >
            <Typography variant="action" className="text-neutral-dark-gray">
              {formatBookingValue(monthlyBookings)} Monthly Bookings
            </Typography>
          </div>
        </div>
      </div>
    </Panel>
  );
};
